<template>
  <div class="max-w-4xl mx-auto p-4 lg:p-6 bg-white shadow-md border border-iron border-gray-200 rounded py-5">
    <h1 class="text-2xl font-bold text-secondary mb-6">Perfil do Instrutor</h1>

    <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
      
      <div class="md:col-span-4 flex flex-col items-center gap-4">
        <div class="relative group">
          <img 
            :src="getInstructorPhoto()" 
            class="w-40 h-40 shadow-lg rounded-full ml-auto mr-auto md:ml-0 md:mr-auto"
          />
          <div class="mt-4">
            <FileUpload 
              mode="basic" 
              name="instructor_photo" 
              accept="image/*" 
              :maxFileSize="1000000" 
              @select="onPhotoSelect" 
              chooseLabel="Alterar Foto"
              class="p-button-sm p-button-outlined"
              :invalidFileTypeMessage="translate('Invalid file type')"
              :invalidFileSizeMessage="translate('invalid file size, file size should be smaller than 1MB', 'ucfirst')"
            />
          </div>
        </div>
        <small class="text-gray-500 text-center">Formatos aceitos: JPG, PNG. Máx 1MB.</small>
      </div>

      <div class="md:col-span-8 flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <label for="name" class="font-bold text-sm text-secondary">Nome Completo</label>
          <InputText id="name" v-model="instructor.name" placeholder="Ex: Nickolas Bini" class="w-full" />
        </div>

        <div class="flex flex-col gap-2">
          <label for="title" class="font-bold text-sm text-secondary">Título / Especialidade</label>
          <div class="flex items-center gap-2 text-secondary">
            <p>({{ translate('english', 'ucfirst') }})</p>
            <InputText id="title" v-model="instructor.title_en" placeholder="Ex: Desenvolvedor Full-stack Pleno" class="w-full" />
          </div>
          <div class="flex items-center gap-2 text-secondary">
            <p>({{ translate('portuguese', 'ucfirst') }})</p>
            <InputText id="title" v-model="instructor.title_pt" placeholder="Ex: Desenvolvedor Full-stack Pleno" class="w-full" />
          </div>
          <div class="flex items-center gap-2 text-secondary">
            <p>({{ translate('spanish', 'ucfirst') }})</p>
            <InputText id="title" v-model="instructor.title_es" placeholder="Ex: Desenvolvedor Full-stack Pleno" class="w-full" />
          </div>
        </div>
      </div>

      <div class="md:col-span-12 flex flex-col gap-2">
        <label class="font-bold text-sm text-secondary">Descrição / Biografia (Inglês)</label>
        <Textarea v-model="instructor.description_en" rows="5" class="w-full" placeholder="Conte sua história acadêmica e profissional..." />
      </div>

      <div class="md:col-span-12 flex flex-col gap-2">
        <label class="font-bold text-sm text-secondary">Descrição / Biografia (Português)</label>
        <Textarea v-model="instructor.description_pt" rows="5" class="w-full" placeholder="Conte sua história acadêmica e profissional..." />
      </div>

      <div class="md:col-span-12 flex flex-col gap-2">
        <label class="font-bold text-sm text-secondary">Descrição / Biografia (Espanhol)</label>
        <Textarea v-model="instructor.description_es" rows="5" class="w-full" placeholder="Conte sua história acadêmica e profissional..." />
      </div>

      <div class="md:col-span-12">
        <div class="flex justify-between items-center mb-4">
          <label class="font-bold text-sm text-secondary">Links Sociais</label>
          <Button icon="pi pi-plus" label="Adicionar Link" @click="addLink" class="p-button-outlined p-button-sm text-carrot" />
        </div>
        
        <div v-for="(link, index) in instructor.links" :key="index" class="flex gap-2 mb-3 items-center">
          <Dropdown 
            v-model="link.icon" 
            :options="socialIcons" 
            optionLabel="name" 
            optionValue="code" 
            placeholder="Ícone" 
            class="w-32"
          >
            <template #value="slotProps">
              <i v-if="slotProps.value" :class="slotProps.value"></i>
              <span v-else>{{ slotProps.placeholder }}</span>
            </template>
            <template #option="slotProps">
              <i :class="slotProps.option.code" class="mr-2"></i>
              <span>{{ slotProps.option.name }}</span>
            </template>
          </Dropdown>
          
          <InputText v-model="link.url" placeholder="https://" class="flex-1" />
          
          <Button icon="pi pi-trash" class="p-button-danger p-button-text" @click="removeLink(index)" />
        </div>
      </div>

      <div class="md:col-span-12 border-t border-secondary pt-6 flex justify-end">
        <Button label="Salvar Perfil" icon="pi pi-check" @click="saveInstructor" :loading="loading" class="w-auto bg-purple-suit-forced text-white p-1" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import FileUpload from 'primevue/fileupload';
import InstructorService from '@/services/InstructorService';
import { showToast } from '@/composables/toast';
import { translate } from '@/composables/translate';
import { useToast } from 'primevue';

const toast = useToast();
const loading = ref(false);
const photoPreview = ref(null);

const instructor = ref({
  id: null,
  name: '',
  title_en: '',
  title_pt: '',
  title_es: '',
  description_en: '',
  description_pt: '',
  description_es: '',
  instructor_photo: null,
  links: []
});

const socialIcons = [
  { name: 'Instagram', code: 'pi pi-instagram' },
  { name: 'LinkedIn', code: 'pi pi-linkedin' },
  { name: 'YouTube', code: 'pi pi-youtube' },
  { name: 'Github', code: 'pi pi-github' },
  { name: 'Site', code: 'pi pi-globe' }
];

onMounted(() => {
  getDefaultInstructor();
});

async function getDefaultInstructor() {
  const defaultInstructor = await InstructorService.getDefaultInstructor();

  if (defaultInstructor) {
    instructor.value = {
      ...defaultInstructor,
      links: defaultInstructor.getLinks()
    };

    if (instructor.value.instructor_photo) {
      instructor.value.instructor_photo = `data:image/jpeg;base64,${instructor.value.instructor_photo}`;
    }

    instructor.value.title_en = defaultInstructor.getTitleByLang('en');
    instructor.value.title_pt = defaultInstructor.getTitleByLang('pt');
    instructor.value.title_es = defaultInstructor.getTitleByLang('es');

    instructor.value.description_en = defaultInstructor.getDescriptionByLang('en');
    instructor.value.description_pt = defaultInstructor.getDescriptionByLang('pt');
    instructor.value.description_es = defaultInstructor.getDescriptionByLang('es');
  }
}

const hasChangedPhoto = ref(false);
const onPhotoSelect = (event) => {
  try {
    const file = event.files[0];
    const reader = new FileReader();

    reader.onload = (e) => {
      photoPreview.value = e.target.result;
      instructor.value.instructor_photo = e.target.result; 
      hasChangedPhoto.value = true;
      
    };
    reader.readAsDataURL(file);
    
  } catch (error) {
    showToast(toast, translate('invalid file'), 'error');
  }
};

const addLink = () => instructor.value.links.push({ url: '', icon: 'pi pi-globe' });
const removeLink = (index) => instructor.value.links.splice(index, 1);

const saveInstructor = async () => {
  if (instructor.value.name.trim() === '') {
    showToast(toast, translate('at least the name is required', 'ucfirst'), 'error');
    return;
  }

  loading.value = true;

  let data = {
    ...instructor.value,
    links: JSON.stringify(instructor.value.links)
  };

  if (!hasChangedPhoto.value) {
    delete data.instructor_photo;
  }

  if (data.title_en || data.title_pt || data.title_es) {
    data['title'] = JSON.stringify({
      en: data.title_en,
      pt: data.title_pt,
      es: data.title_es
    });
  }

  if (data.description_en || data.description_pt || data.description_es) {
    data['description'] = JSON.stringify({
      en: data.description_en,
      pt: data.description_pt,
      es: data.description_es
    });
  }

  const response = await InstructorService.save(data);

  if (!response.success()) {
    showToast(toast, response.getMessage(), 'error');
  }

  instructor.value.id = response.getContent().id;
  showToast(toast, translate('instructor saved with success'), 'ucfirst');

  loading.value = false;
};

/**
 * Gets Instructor photo
 */
function getInstructorPhoto() {
  const photo = instructor.value.instructor_photo != null 
    ? instructor.value.instructor_photo
    : require('@/assets/images/avatar.png');

  return photo;
}

</script>